import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Home, User, TrendingUp, Calendar, Trophy, Info, LogOut, Menu, X, Settings, Users, Sun, Moon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const { theme, toggleTheme } = useTheme();

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/';
  };

  // Scroll to top and navigate
  const handleNavClick = (path) => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const navItems = [
    { path: '/', label: 'HOME', icon: Home },
    { path: '/voorspel', label: 'VOORSPEL', icon: TrendingUp },
    { path: '/voorspellingen', label: 'VOORSPELLINGEN', icon: User },
    { path: '/stand', label: 'STAND', icon: Trophy },
    { path: '/kalender', label: 'KALENDER', icon: Calendar },
    { path: '/prijzen', label: 'PRIJZEN', icon: Trophy },
    { path: '/speluitleg', label: 'SPELUITLEG', icon: Info },
    { path: '/over-ons', label: 'OVER ONS', icon: Users },
  ];

  // Theme toggle button component
  const ThemeToggle = ({ className = "" }) => (
    <button
      onClick={toggleTheme}
      className={`p-2 rounded-md transition-all duration-200 ${className}`}
      aria-label={theme === 'dark' ? 'Schakel naar lichte modus' : 'Schakel naar donkere modus'}
      title={theme === 'dark' ? 'Lichte modus' : 'Donkere modus'}
    >
      {theme === 'dark' ? (
        <Sun size={20} className="text-[#FDB913]" />
      ) : (
        <Moon size={20} className="text-[#FDB913]" />
      )}
    </button>
  );

  return (
    <nav className="bg-gradient-to-r from-[#1a2332] to-[#2d3e50] border-b-4 border-[#FDB913] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0" onClick={() => window.scrollTo(0, 0)}>
            <img 
              src="/images/logo.png" 
              alt="Formule 1 Pool" 
              className="h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => handleNavClick(item.path)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center space-x-1 whitespace-nowrap ${
                    isActive
                      ? 'bg-[#FDB913] text-[#1a2332]'
                      : 'text-white hover:bg-[#FDB913] hover:text-[#1a2332]'
                  }`}
                >
                  <Icon size={14} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* User Section with Theme Toggle - Desktop */}
          <div className="hidden lg:flex items-center space-x-3">
            {user ? (
              <>
                {(user.role === 'admin' || user.role === 'super_admin') && (
                  <Link to="/admin" onClick={() => window.scrollTo(0, 0)}>
                    <Button
                      variant="ghost"
                      className="text-white hover:bg-[#FDB913] hover:text-[#1a2332]"
                    >
                      <Settings size={16} className="mr-2" />
                      Admin
                    </Button>
                  </Link>
                )}
                <Link to="/profiel" onClick={() => window.scrollTo(0, 0)}>
                  <Button
                    variant="ghost"
                    className="text-white hover:bg-[#FDB913] hover:text-[#1a2332]"
                  >
                    <User size={16} className="mr-2" />
                    {user.username}
                  </Button>
                </Link>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="text-white hover:bg-red-500 hover:text-white"
                >
                  <LogOut size={16} className="mr-2" />
                  Uitloggen
                </Button>
              </>
            ) : (
              <Link to="/login" onClick={() => window.scrollTo(0, 0)}>
                <Button className="bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-semibold">
                  Inloggen
                </Button>
              </Link>
            )}
            {/* Desktop Theme Toggle */}
            <ThemeToggle className="hover:bg-white/10" />
          </div>

          {/* Mobile: Theme Toggle + Menu button */}
          <div className="lg:hidden flex items-center space-x-2">
            <ThemeToggle className="hover:bg-white/10" />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white p-2"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-4">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => handleNavClick(item.path)}
                  className={`block px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 flex items-center space-x-2 mb-1 ${
                    isActive
                      ? 'bg-[#FDB913] text-[#1a2332]'
                      : 'text-white hover:bg-[#FDB913] hover:text-[#1a2332]'
                  }`}
                >
                  <Icon size={16} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            <div className="mt-4 pt-4 border-t border-gray-600">
              {user ? (
                <>
                  {(user.role === 'admin' || user.role === 'super_admin') && (
                    <Link
                      to="/admin"
                      onClick={() => handleNavClick('/admin')}
                      className="block px-4 py-3 text-white hover:bg-[#FDB913] hover:text-[#1a2332] rounded-md mb-2"
                    >
                      <Settings size={16} className="inline mr-2" />
                      Admin
                    </Link>
                  )}
                  <Link
                    to="/profiel"
                    onClick={() => handleNavClick('/profiel')}
                    className="block px-4 py-3 text-white hover:bg-[#FDB913] hover:text-[#1a2332] rounded-md mb-2"
                  >
                    <User size={16} className="inline mr-2" />
                    {user.username}
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-4 py-3 text-white hover:bg-red-500 rounded-md"
                  >
                    <LogOut size={16} className="inline mr-2" />
                    Uitloggen
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => handleNavClick('/login')}
                >
                  <Button className="w-full bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-semibold">
                    Inloggen
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
